import { Injectable } from '@angular/core';
import { forkJoin, map, Observable, of } from 'rxjs';
import { ENDPOINTS } from '../../../../shared/constants/url.constants';
import { ListGridRequestPayload } from '../../../../shared/interfaces/list.interface';
import { HttpRequestService } from '../../../../shared/services/http-request.service';
import { formatUrl } from '../../../../shared/utils/format-url-util';

/** One row of the batch criteria builder. */
export interface PayrollCriterion {
  columnLkp: string;
  operatorLkp: string;
  value: string;
  /** Upper bound of a Between range; sent only for that operator. */
  valueEnd?: string;
}

/** POST /batch/create — batchType / dataType carry lkpValueCodes from domains 318 / 319. */
export interface CreateBatchRequest {
  programCode: string;
  batchType: string;
  dataType: string;
  /** The date the batch pays out on, as mm/dd/yyyy. Never in the past. */
  checkDate: string;
  /** Optional lkpValueCode from the ProviderType domain; empty means every provider type. */
  providerType: string;
  criteria: PayrollCriterion[];
}

/** GET /batch-results row. */
export interface PayrollBatchResultRow {
  payrollBatchId: number;
  batchInstanceId: number;
  timesheetDetailId: number;
  providerId: string;
  employerId: string;
  consumerId: string;
  serviceDate: string;
  processingStatus: string;
  businessStatus: string;
  paidUnits: number;
  servicecodeRate: number;
  /** Line batch status code, e.g. ADNOPAIR; lineBatchStatus is its description. */
  status: string;
  lineBatchStatus: string;
}

/** Data access for Payroll Processing — base path /payroll/v1/payroll-processing. */
@Injectable({ providedIn: 'root' })
export class TimesheetPayrollProcessingService {
  constructor(private http: HttpRequestService) {}

  /** Batch-run list backing the main grid. */
  getPayrollRuns(payload: ListGridRequestPayload): Observable<any> {
    return this.http.post(ENDPOINTS.TIMESHEET_PAYROLL_PROCESSING.LIST_PAGE_DATA, payload);
  }

  /**
   * Criteria a batch was created with. Reuses the existing shared mass-batch
   * details API rather than a payroll-specific one.
   */
  getBatchDetails(programCode: string, batchInstanceId: string | number): Observable<any> {
    const url = formatUrl(ENDPOINTS.MASS_ADJUSTMENT.GET_DETAILS, {
      programCode,
      batchInstanceId: String(batchInstanceId),
    });
    return this.http.get(url).pipe(map((response: any) => response?.responsedata ?? response));
  }

  createBatch(request: CreateBatchRequest): Observable<number | null> {
    return this.http
      .post(ENDPOINTS.TIMESHEET_PAYROLL_PROCESSING.CREATE_BATCH, request, true)
      .pipe(map((response: any) => response?.data?.batchInstanceId ?? null));
  }

  /**
   * Timesheets a batch picked up. Paged with offset/limit and returns no total,
   * so callers accumulate pages rather than showing a record count.
   */
  getBatchResults(
    programCode: string,
    batchInstanceId: string | number,
    offset: number,
    limit: number
  ): Observable<PayrollBatchResultRow[]> {
    const query = new URLSearchParams({
      programCode,
      batchInstanceId: String(batchInstanceId),
      offset: String(offset),
      limit: String(limit),
    });
    return this.http
      .get(`${ENDPOINTS.TIMESHEET_PAYROLL_PROCESSING.BATCH_RESULTS}?${query.toString()}`)
      .pipe(map((response: any) => response?.responsedata ?? []));
  }

  /** Drops selected result rows from the batch before it is approved. */
  excludeRows(
    programCode: string,
    batchInstanceId: string | number,
    rowIds: number[],
    remarks: string
  ): Observable<unknown> {
    return this.http.post(
      ENDPOINTS.TIMESHEET_PAYROLL_PROCESSING.EXCLUDE_ROWS,
      { programCode, batchInstanceId: Number(batchInstanceId), rowIds, remarks },
      true
    );
  }

  /**
   * Approve / reject the given batches.
   *
   * Both endpoints act on a single batchInstanceId, so a multi-row selection
   * fans out into one request per batch.
   */
  updateBatchStatus(
    programCode: string,
    batchInstanceIds: Array<string | number>,
    approve: boolean,
    remarks: string
  ): Observable<unknown[]> {
    if (!batchInstanceIds.length) {
      return of([]);
    }

    const url = approve
      ? ENDPOINTS.TIMESHEET_PAYROLL_PROCESSING.APPROVE_BATCH
      : ENDPOINTS.TIMESHEET_PAYROLL_PROCESSING.REJECT_BATCH;

    return forkJoin(
      batchInstanceIds.map((batchInstanceId) =>
        this.http.post(url, { programCode, batchInstanceId: Number(batchInstanceId), remarks }, true)
      )
    );
  }
}
